import React, { useState, useEffect, useCallback } from 'react'
import { useCalendarEvents, type CalendarEvent } from '@/hooks/useCalendarEvents'
import { useAssistants } from '@/hooks/useAssistants'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import CalendarView from '@/components/agenda/CalendarView'
import EventList from '@/components/agenda/EventList'
import EventDetailModal from '@/components/agenda/EventDetailModal'
import { AssistantSelector } from '@/components/agenda/AssistantSelector'
import { Calendar, Settings } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface ConnectedAssistant {
  id: string
  name: string
  is_active: boolean
  google_email: string | null
}

const Agenda: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null)
  const [connectedAssistants, setConnectedAssistants] = useState<ConnectedAssistant[]>([])
  const [loadingConnections, setLoadingConnections] = useState(true)

  const { events, loading: eventsLoading, fetchEvents, getEventsForDate, clearEvents } = useCalendarEvents()
  const { assistants, loading: assistantsLoading } = useAssistants()

  const accountId = user?.account_id

  useEffect(() => {
    const fetchConnectedAssistants = async () => {
      if (!accountId || !supabase) {
        setLoadingConnections(false)
        return
      }

      try {
        setLoadingConnections(true)

        const { data: integrations, error } = await supabase
          .from('account_assistant_google_integrations')
          .select('assistant_id, google_email')
          .eq('account_id', accountId)
          .eq('is_active', true)

        if (error) {
          console.error('[Agenda] Error fetching integrations:', error)
          setConnectedAssistants([])
          return
        }

        if (!integrations || integrations.length === 0) {
          setConnectedAssistants([])
          return
        }

        const connected = assistants
          .filter(a => a.is_active && integrations.some(i => i.assistant_id === a.id))
          .map(a => ({
            id: a.id,
            name: a.name,
            is_active: a.is_active,
            google_email: integrations.find(i => i.assistant_id === a.id)?.google_email || null
          }))

        setConnectedAssistants(connected)

        if (connected.length > 0 && !selectedAssistantId) {
          setSelectedAssistantId(connected[0].id)
        } else if (connected.length === 0) {
          setSelectedAssistantId(null)
        } else if (selectedAssistantId && !connected.find(c => c.id === selectedAssistantId)) {
          setSelectedAssistantId(connected[0]?.id || null)
        }
      } catch (err) {
        console.error('[Agenda] Unexpected error:', err)
        setConnectedAssistants([])
      } finally {
        setLoadingConnections(false)
      }
    }

    if (!assistantsLoading) {
      fetchConnectedAssistants()
    }
  }, [accountId, assistants, assistantsLoading])

  const handleMonthChange = useCallback((date: Date) => {
    if (selectedAssistantId) {
      fetchEvents(date, selectedAssistantId)
    }
  }, [fetchEvents, selectedAssistantId])

  useEffect(() => {
    if (selectedAssistantId && !loadingConnections) {
      fetchEvents(selectedDate, selectedAssistantId)
    } else {
      clearEvents()
    }
  }, [selectedAssistantId, loadingConnections])

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
  }

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedEvent(null)
  }

  const handleAssistantChange = (assistantId: string | null) => {
    setSelectedAssistantId(assistantId)
  }

  const selectedDateEvents = getEventsForDate(selectedDate)
  const selectedAssistant = connectedAssistants.find(a => a.id === selectedAssistantId)

  if (loadingConnections || assistantsLoading) {
    return (
      <div className="w-full h-[calc(100vh-110px)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin dark:border-[#60A5FA]" />
      </div>
    )
  }

  if (connectedAssistants.length === 0) {
    return (
      <div className="w-full h-[calc(100vh-110px)] flex flex-col">
        <div className="mb-4 pb-4 border-b border-border">
          <h2 className="text-[26px] sm:text-[30px] font-semibold text-foreground dark:text-neutral-50">
            Agenda
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-5 max-w-md text-center px-6 py-12">
            <div className="w-16 h-16 rounded-full bg-[#DBEAFE] flex items-center justify-center dark:bg-blue-500/20">
              <Calendar className="w-8 h-8 text-[#2563EB] dark:text-[#60A5FA]" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] dark:text-neutral-50">
              Nenhum assistente conectado
            </h3>
            <p className="text-[#64748B] dark:text-neutral-400">
              Para visualizar a agenda, conecte o Google Calendar em pelo menos um assistente.
            </p>
            <button
              onClick={() => navigate('/assistentes')}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-[12px] shadow-sm transition-colors duration-200"
            >
              <Settings className="w-4 h-4" />
              Configurar Assistentes
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-[calc(100vh-110px)] flex flex-col">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-border">
        <div className="flex items-center gap-4">
          <h2 className="text-[26px] sm:text-[30px] font-semibold text-[#0F172A] dark:text-neutral-50">
            Agenda
          </h2>
          <AssistantSelector
            assistants={connectedAssistants}
            selectedId={selectedAssistantId}
            onChange={handleAssistantChange}
            loading={loadingConnections}
          />
        </div>
        {selectedAssistant?.google_email && (
          <div className="flex items-center gap-2 text-sm text-[#64748B] dark:text-neutral-400">
            <Calendar className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" />
            <span>{selectedAssistant.google_email}</span>
          </div>
        )}
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
        <div className="lg:col-span-5 xl:col-span-4">
          <CalendarView
            events={events}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onMonthChange={handleMonthChange}
            loading={eventsLoading}
          />
        </div>

        <div className="lg:col-span-7 xl:col-span-8 min-h-0">
          <EventList
            events={selectedDateEvents}
            selectedDate={selectedDate}
            loading={eventsLoading}
            onEventClick={handleEventClick}
          />
        </div>
      </div>

      <EventDetailModal
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default Agenda
